import React, {
  CSSProperties,
  ReactNode,
  useEffect,
  useRef,
  useState
} from 'react';
import { createPortal } from 'react-dom';
import classnames from 'classnames';

import {
  TOOLTIP_POSITIONS,
  TOOLTIP_VARIANTS
} from 'dpl/common/utils/constants';
import {
  PORTAL_MOUNT_EL,
  INITIAL_POSITION,
  getTooltipPosition
} from 'dpl/common/tooltip/components/FloatingTooltipWrapper';
import FloatingTooltip from 'dpl/common/tooltip/components/FloatingTooltip';

// workaround to avoid type eslint errors
const FLOATING_TOOLTIP_WRAPPER_TOOLTIP_CLASS =
  'FloatingTooltipWrapper__tooltip';

interface PortalPosition {
  left: number;
  top: number;
  width: number;
}

export interface UserFollowSuccessPopoverProps {
  children: ReactNode;
  isVisible?: boolean;
  title: ReactNode;
  targetEl?: HTMLElement | null;
  portalStyle?: CSSProperties | null;
}

export default function UserFollowSuccessPopover({
  children,
  isVisible = false,
  title,
  targetEl = PORTAL_MOUNT_EL as HTMLElement | null,
  portalStyle = null
}: UserFollowSuccessPopoverProps) {
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);

  const [portalPosition, setPortalPosition] = useState<PortalPosition>(
    INITIAL_POSITION as PortalPosition
  );
  const [leftOffset, setLeftOffset] = useState(0);

  useEffect(() => {
    if (!isVisible) {
      window.setTimeout(() => {
        setPortalPosition(INITIAL_POSITION as PortalPosition);
      }, 300);
    } else if (targetEl && anchorRef.current && tooltipRef.current) {
      const {
        leftOffset: tooltipLeftOffset,
        portalPosition: tooltipPortalPosition
      } = getTooltipPosition(
        targetEl,
        anchorRef.current,
        tooltipRef.current,
        TOOLTIP_POSITIONS.BOTTOM_CENTER
      );

      setLeftOffset(tooltipLeftOffset);
      setPortalPosition(tooltipPortalPosition);
    }
  }, [isVisible, targetEl]);

  return (
    <div className="UserFollowSuccessPopover FloatingTooltipWrapper dib">
      <div className="pointer flex" ref={anchorRef}>
        {children}
      </div>
      {targetEl &&
        createPortal(
          <div
            className="z-max absolute"
            style={{
              left: `${portalPosition.left}px`,
              top: `${portalPosition.top}px`,
              width: `${portalPosition.width}px`,
              ...(portalStyle || {})
            }}
          >
            <div
              style={{ marginLeft: `${leftOffset}px` }}
              className={classnames('mt2 min-w-100 tc transform-center-x', {
                'pointer-events-none': !isVisible
              })}
            >
              <div
                className={classnames('transition nowrap', {
                  'FloatingTooltipWrapper__tooltip--visible': isVisible,
                  [FLOATING_TOOLTIP_WRAPPER_TOOLTIP_CLASS]: !isVisible
                })}
              >
                <FloatingTooltip
                  setRef={tooltipRef}
                  arrowOffset={leftOffset}
                  omitArrow
                  // @ts-ignore
                  title={title}
                  minWidth="200px"
                  variant={TOOLTIP_VARIANTS.LIGHT}
                />
              </div>
            </div>
          </div>,
          targetEl
        )}
    </div>
  );
}
